import { useState } from "react";

import { useAPI } from "./useAPI";
import { useSwrFetch } from "./useSwrFetch";

interface MicroReviewStatus {
  can_review: boolean;
  review_exists: boolean;
  is_positive?: boolean | null;
}

interface SubmitReviewResponse {
  success: boolean;
  message: string;
  data?: {
    review_id: number;
  };
}

interface UseShipmentMicroReviewProps {
  shipmentId: number;
  onSuccess?: VoidFunction;
}

interface UseShipmentMicroReviewResult {
  canReview: boolean;
  reviewExists: boolean;
  isPositive: boolean | null;
  loading: boolean;
  error: string | null;
  submitReview: (
    isPositive: boolean,
    onSubmitSuccess?: VoidFunction,
  ) => Promise<void>;
  submitting: boolean;
  submitError: string | null;
}

export function useShipmentMicroReview(
  props: UseShipmentMicroReviewProps,
): UseShipmentMicroReviewResult {
  const { shipmentId, onSuccess } = props;
  const { api } = useAPI();

  const { data, loading, error, mutate } = useSwrFetch<MicroReviewStatus>({
    url: `/shipments/${shipmentId}/micro-review-status`,
    errorMessage: "Failed to check review status",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitReview = async (
    isPositive: boolean,
    onSubmitSuccess?: VoidFunction,
  ) => {
    try {
      setSubmitting(true);
      setSubmitError(null);

      const response = await api<SubmitReviewResponse>(
        "post",
        "/carriers/shipment-micro-reviews",
        {
          shipment_id: shipmentId,
          is_positive: isPositive,
        },
      );

      if (response.status >= 200 && response.status < 300) {
        mutate(
          { can_review: false, review_exists: true, is_positive: isPositive },
          false,
        );
        onSuccess?.();
        onSubmitSuccess?.();
      } else {
        setSubmitError("Failed to submit review");
      }
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.response?.data?.detail ??
          "Failed to submit review",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    canReview: data?.can_review ?? false,
    reviewExists: data?.review_exists ?? false,
    isPositive: data?.is_positive ?? null,
    loading,
    error,
    submitReview,
    submitting,
    submitError,
  };
}
